import { createRoot } from "react-dom/client";
import "./lib/pwa-install";
import "./i18n";
import App from "./App.tsx";
import "./index.css";
// Marca PULSO: as animações do radar (pulso-sweep/ping/core/blip) vivem em
// zappy.css, mas a SplashScreen/ZappyLoading renderizam em qualquer rota —
// o CSS precisa ser global ou o radar aparece parado na abertura do app.
import "./zappy/zappy.css";
import { isRunningStandalone } from "./lib/pwa";
import { getOfficialAppUrl, shouldForceOfficialInstallSource } from "./lib/pwa";
import { initPwaState, setPwaState } from "./lib/pwa-update";
import { installGlobalErrorHandlers } from "./lib/error-logger";
import { installChunkReloadHandler } from "./lib/chunk-reload";
import { installBootTelemetry, scheduleBlankScreenCheck } from "./lib/boot-telemetry";
import { runAfterLoad, runWhenIdle } from "./lib/idle";
import { installRlsInterceptor } from "./lib/security/installRlsInterceptor";
import { installAuthRefreshInterceptor } from "./lib/auth/installAuthRefreshInterceptor";
import { detectExpiredSessionAtBoot } from "./lib/auth/detectExpiredSessionAtBoot";
import { enforceEphemeralSessionBoot } from "./lib/auth/ephemeralSession";
import { migrateLegacyBrandStorage } from "./lib/brand-storage-migration";
import { initSentry } from "./lib/sentry";
import { validateRequiredEnv } from "./lib/env-validation";
import { applyStoredAccessibilityPrefs } from "./lib/accessibility/applyStoredPrefs";
import { installDynamicType } from "./lib/accessibility/dynamicType";

applyStoredAccessibilityPrefs();
installDynamicType();

const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev";
initPwaState(appVersion);

// Críticos: precisam estar em pé ANTES do render para capturar erros de boot.
migrateLegacyBrandStorage();
// Sentry SDK oficial — NO-OP se VITE_SENTRY_DSN não estiver no workspace.
// Roda síncrono antes do render para já capturar erros de boot via Sentry
// (em paralelo ao sentry-lite, que envia para a tabela client_error_logs).
initSentry();
installBootTelemetry();
validateRequiredEnv();
installGlobalErrorHandlers();
// Recupera automaticamente de chunks ausentes após um novo deploy.
installChunkReloadHandler();
installAuthRefreshInterceptor();
// Precisa rodar ANTES de qualquer código que importe o supabase client,
// para limpar tokens efêmeros antes do getSession() ler do storage.
enforceEphemeralSessionBoot();
detectExpiredSessionAtBoot();
installRlsInterceptor();
scheduleBlankScreenCheck("root");

// Desabilita restauração nativa de scroll do navegador — assumimos
// controle manual via <ScrollRestoration /> para sincronizar com a
// animação de transição de rota (sem saltos visuais).
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  try {
    window.history.scrollRestoration = "manual";
  } catch {
    /* ignore */
  }
}

// PERF: libera bf-cache (back/forward cache). O Supabase Realtime adquire
// Web Locks ("LockManager: WebLocksContention") que bloqueiam a entrada da
// página no bf-cache, fazendo o browser reconstruir a página inteira ao
// voltar — métrica direta de LCP. Em `pagehide` (persisted=true) fechamos
// canais Realtime; em `pageshow` (persisted=true) eles serão reabertos pelos
// hooks ao remontar. Não usamos `unload` (também bloqueia bf-cache).
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", (event) => {
    if (!event.persisted) return;
    import("@/integrations/supabase/client")
      .then(({ supabase }) => {
        try {
          supabase.removeAllChannels();
        } catch {
          /* ignore */
        }
      })
      .catch(() => {});
  });
}

// Não-críticos: deferidos para depois do first paint (não competem com FCP/TTI).
runWhenIdle(() => {
  import("./lib/sentry-lite").then((m) => m.initSentryLite()).catch(() => {});
  import("./lib/web-vitals").then((m) => m.initWebVitals()).catch(() => {});
});

// Detect preview/iframe environments where service workers cause stale caching
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const hostname = typeof window !== "undefined" ? window.location.hostname : "";
// Ambientes de preview/sandbox: NÃO devem redirecionar para o host oficial nem
// registrar o service worker (cache stale de ícones/logomarca). Além dos hosts
// da Lovable, cobrimos os domínios de sandbox (ex.: *.sandbox.novita.ai), dev
// local e plataformas de preview por iframe/WebContainer.
const isPreviewHost =
  hostname.includes("id-preview--") ||
  hostname.includes("lovableproject.com") ||
  hostname.includes("lovable.app") ||
  hostname.includes("sandbox.novita.ai") ||
  hostname.includes("novita.ai") ||
  hostname.endsWith(".sandbox") ||
  hostname.includes("webcontainer") ||
  hostname.includes("stackblitz") ||
  hostname.includes("codesandbox") ||
  hostname.includes("gitpod") ||
  hostname.includes("replit") ||
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "0.0.0.0";

const shouldRedirectToOfficialHost =
  typeof window !== "undefined" &&
  !isInIframe &&
  !isPreviewHost &&
  shouldForceOfficialInstallSource(hostname);

if (shouldRedirectToOfficialHost) {
  const targetUrl = getOfficialAppUrl(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );

  if (window.location.href !== targetUrl) {
    window.location.replace(targetUrl);
  }
}

if ("serviceWorker" in navigator) {
  if (isPreviewHost || isInIframe) {
    // Em preview/iframe limpamos SWs imediatamente para evitar cache stale.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => {}));
    });
    if ("caches" in window) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k).catch(() => {})));
    }
  } else {
    // Defer registro do SW para após `load` — não afeta o primeiro paint.
    runAfterLoad(() => {
      import("virtual:pwa-register").then(({ registerSW }) => {
        const updateSW = registerSW({
          immediate: true,
          onRegisteredSW(_swUrl, registration) {
            setPwaState({ state: "active", update: () => updateSW(true) });
            if (registration) {
              setInterval(() => {
                registration.update().catch(() => {});
              }, 60_000);
            }
          },
          onNeedRefresh() {
            setPwaState({ state: "waiting", needsUpdate: true });
            // Nova versão do app disponível — avisa o usuário e oferece
            // ação para atualizar imediatamente (ativa o novo SW + reload).
            import("sonner").then(({ toast }) => {
              toast("Nova versão disponível", {
                description:
                  "Atualize o app para garantir que a navegação funcione na versão mais recente.",
                duration: Infinity,
                action: {
                  label: "Atualizar",
                  onClick: () => {
                    updateSW(true).catch(() => {
                      window.location.reload();
                    });
                  },
                },
              });
            }).catch(() => {});
          },
          onRegisterError() {
            setPwaState({ state: "error" });
          },
        });
      }).catch(() => {});

      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    });
}

// Cache-busting dos ícones: se a logomarca mudou neste deploy, limpamos as
// entradas antigas do Cache Storage e reapontamos favicon/apple-touch/manifest
// para a URL versionada, exibindo a arte nova sem esperar expiração de cache.
runAfterLoad(() => {
  import("./lib/pwa/iconCacheBusting")
    .then((m) => m.ensureFreshIcons())
    .catch(() => {});
});
}

// Initialize native plugins (no-op on web) — deferido p/ não bloquear boot web.
runWhenIdle(() => {
  import("./lib/capacitor").then((m) => m.initCapacitorPlugins()).catch(() => {});
});

// Não reescrevemos rotas internas para a Landing no boot. Isso causava um
// ciclo visual em PWA/Android: tela atual → Landing com redirect → tela atual,
// percebido como piscada em praticamente todas as telas após reload/retorno.
// A única normalização mantida é /index → /, sem parâmetro redirect.
if (typeof window !== "undefined") {
  const currentPath = window.location.pathname;

  if (currentPath === "/index") {
    window.history.replaceState(null, "", `/${window.location.search}${window.location.hash}`);
  }
  if (isRunningStandalone()) {
    runWhenIdle(() => {
      import("./lib/capacitor")
        .then((m) => m.installReturnToLandingHandlers())
        .catch(() => {});
    });
  }
}

createRoot(document.getElementById("root")!).render(<App />);
